import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { DataOrModifiedFn, resourceCache, UpdaterFn, useAsyncResource } from 'use-async-resource';
import { ShortDevicePassport } from '../models/warehouse-models';
import { useGetAllShortDevicePassports, useGetAllShortMaterialPassports } from '../utils/warehouses-hooks';
import { BranchListItem, DashboardBranch, MapBranch, MapBranches, Reminder, Trouble } from '../models/dashboard-models';
import {
    DASHBOARD_BRANCHES_PAGE_SIZE,
    fetchDashboardBranchById,
    fetchDashboardBranches,
    fetchMapBranchById,
    fetchMapBranches,
    useGetRemindersYours,
    useGetTroublesAll,
} from '../utils/dashboard-hooks';
import { Filter, Pager } from '../models/table-models';

export interface BranchesContextProps {
    lastSelectedBranch?: number | null;
    setLastSelectedBranch: (id: number | null) => void;
    getMapBranches: DataOrModifiedFn<MapBranches>;
    branchesFilter: Filter;
    setBranchesFilter: React.Dispatch<React.SetStateAction<Filter>>;
    selectBranch: (branch: MapBranch | BranchListItem) => Promise<void>;
    selectedBranch: MapBranch;
    selectedBranches: MapBranch[];
    setSelectedBranches: React.Dispatch<React.SetStateAction<MapBranch[]>>;
    dashboardBranches: DashboardBranch[];
    branches: DashboardBranch[];
    setBranches: React.Dispatch<React.SetStateAction<DashboardBranch[]>>;
    mapBranches: MapBranch[];
    setMapBranches: React.Dispatch<React.SetStateAction<MapBranch[]>>;
    taskManagerBranches: DashboardBranch[];
    setTaskManagerBranches: React.Dispatch<React.SetStateAction<DashboardBranch[]>>;
    loadMoreDashboardBranches: () => Promise<void>;
    hasMoreDashboardBranches: boolean;
    dashboardBranchesLoading: boolean;
    filterBranchesByState: (state: string) => void;
    filterBranchesByGroup: (group: string | undefined) => void;
    filterBranchesByUser: (id: number | undefined) => void;
    filterBranchesBySearch: (search: string) => void;
    newMarker: Position;
    setNewMarker: React.Dispatch<React.SetStateAction<Position>>;
    getReminders: DataOrModifiedFn<Reminder[]>;
    fetchNewReminders: UpdaterFn<[]>;
    materialsPassports: ShortDevicePassport[];
    devicePassports: ShortDevicePassport[];
    getTroubles: DataOrModifiedFn<Trouble[]>;
    fetchNewTroubles: UpdaterFn<[]>;
    getNewBranches: UpdaterFn<
        [
            {
                filter: {} | undefined;
            }
        ]
    >;
    // Optimistic update methods to avoid refetching all 5000+ branches
    addBranchLocal: (branch: MapBranch) => void;
    updateBranchLocal: (branch: MapBranch) => void;
    removeBranchLocal: (branchId: number) => void;
    refreshBranches: () => void;
}

export interface Position {
    lat: number;
    lng: number;
}

export const BranchesContext = createContext({} as BranchesContextProps);

const INITIAL_BRANCHES_FILTER: Filter = { search: '', tags: [] };
const INITIAL_MAP_BRANCHES_REQUEST = { filter: undefined as {} | undefined };
const INITIAL_DASHBOARD_BRANCHES_REQUEST = {
    filter: {
        page: 1,
        itemsPerPage: DASHBOARD_BRANCHES_PAGE_SIZE,
    },
};

export const BranchesProvider: React.FC = ({ children }) => {
    const [selectedBranch, setSelectedBranch] = useState<MapBranch>({} as MapBranch);
    const [selectedBranches, setSelectedBranches] = useState<MapBranch[]>([]);
    const [newMarker, setNewMarker] = useState({} as Position);
    const [branchesFilter, setBranchesFilter] = useState<Filter>(INITIAL_BRANCHES_FILTER);
    const hasActiveBranchFilter = useCallback((filter: Filter) => {
        return (
            filter.search.length >= 1 ||
            Boolean(filter.selectedGroup) ||
            Boolean(filter.assignedDistributor) ||
            Boolean(filter.state)
        );
    }, []);
    const getMapBranchesFilter = useCallback((filter: Filter = branchesFilter) => {
        return hasActiveBranchFilter(filter) ? filter : undefined;
    }, [branchesFilter, hasActiveBranchFilter]);
    const [getMapBranches, getNewBranches] = useAsyncResource(fetchMapBranches, INITIAL_MAP_BRANCHES_REQUEST);
    const getDashboardBranchesFilter = useCallback((page: number, filter: Filter = branchesFilter) => ({
        ...(hasActiveBranchFilter(filter) ? filter : {}),
        page,
        itemsPerPage: DASHBOARD_BRANCHES_PAGE_SIZE,
    }), [branchesFilter, hasActiveBranchFilter]);
    const [getDashboardBranches, getNewDashboardBranches] = useAsyncResource(
        fetchDashboardBranches,
        INITIAL_DASHBOARD_BRANCHES_REQUEST
    );
    const [getAllMaterials] = useAsyncResource(useGetAllShortMaterialPassports, false);

    const [getAllDevices] = useAsyncResource(useGetAllShortDevicePassports, false);
    const allMapBranches = getMapBranches();

    const devicePassports = getAllDevices();
    const materialsPassports = getAllMaterials();
    const dashboardBranchesData = getDashboardBranches();
    const dashboardBranchItems = dashboardBranchesData.items ?? [];
    const mapBranchItems = allMapBranches.items ?? [];

    const [branches, setBranches] = useState<DashboardBranch[]>(dashboardBranchItems);
    const [dashboardBranches, setDashboardBranches] = useState<DashboardBranch[]>(dashboardBranchItems);
    const [mapBranches, setMapBranches] = useState<MapBranch[]>(mapBranchItems);
    const [dashboardPager, setDashboardPager] = useState<Pager | undefined>(dashboardBranchesData.pager);
    const [dashboardBranchesLoading, setDashboardBranchesLoading] = useState(false);
    const dashboardBranchesLoadingRef = useRef(false);

    const [lastSelectedBranch, setLastSelectedBranch] = useState<number | null>();

    const [taskManagerBranches, setTaskManagerBranches] = useState<DashboardBranch[]>(dashboardBranchItems);
    const [getReminders, fetchNewReminders] = useGetRemindersYours();
    const [getTroubles, fetchNewTroubles] = useGetTroublesAll();

    const hasMoreDashboardBranches =
        typeof dashboardPager?.page === 'number'
        && typeof dashboardPager?.pagesCount === 'number'
        && dashboardPager.page < dashboardPager.pagesCount;

    const appendUniqueDashboardBranches = useCallback((current: DashboardBranch[], incoming: DashboardBranch[]) => {
        const existingIds = new Set(current.map((branch) => branch.id));
        const uniqueIncoming = incoming.filter((branch) => !existingIds.has(branch.id));
        return [...current, ...uniqueIncoming];
    }, []);

    const mergeBranchData = useCallback((...branchesToMerge: Array<Partial<MapBranch> | undefined>) => {
        return branchesToMerge.reduce<Partial<MapBranch>>((mergedBranch, branch) => {
            if (!branch) {
                return mergedBranch;
            }

            Object.entries(branch).forEach(([key, value]) => {
                if (value !== undefined) {
                    (mergedBranch as Record<string, unknown>)[key] = value;
                }
            });

            return mergedBranch;
        }, {});
    }, []);

    const toDashboardBranch = useCallback((branch: MapBranch): DashboardBranch => ({
        id: branch.id,
        name: branch.name ?? '',
        state: branch.state,
        companyName: branch.companyName ?? '',
        branchAddress: branch.branchAddress,
        city: branch.city,
        phone: branch.phone,
        assignedDistributor: branch.assignedDistributor,
        assignedTask: branch.assignedTask,
        maintenanceDays: branch.maintenanceDays,
        groupName: branch.groupName,
        price: branch.price,
        paymentDate: branch.paymentDate,
        nextMaintenanceDate: branch.nextMaintenanceDate,
        maintenanceState: branch.maintenanceState,
    }), []);

    const isMapBranch = (branch: MapBranch | BranchListItem): branch is MapBranch => {
        return typeof (branch as MapBranch).latitude === 'string' && typeof (branch as MapBranch).longitude === 'string';
    };

    const getFilteredMapBranches = useCallback((filter: Filter, sourceBranches: MapBranch[] = mapBranchItems) => {
        if (!hasActiveBranchFilter(filter)) {
            return sourceBranches;
        }

        const selectedState = filter.state?.toLowerCase();
        const selectedGroup = filter.selectedGroup?.toLowerCase();
        const selectedDistributor = filter.assignedDistributor ? String(filter.assignedDistributor) : undefined;

        return sourceBranches.filter((branch) => {
            const matchesState = !selectedState || branch.state?.toLowerCase() === selectedState;
            const matchesGroup = !selectedGroup || branch.groupName?.toLowerCase() === selectedGroup;
            const matchesDistributor =
                !selectedDistributor || String(branch.assignedDistributor) === selectedDistributor;

            return matchesState && matchesGroup && matchesDistributor;
        });
    }, [hasActiveBranchFilter, mapBranchItems]);

    const getHydratedMapBranch = async (branchToResolve: MapBranch | BranchListItem): Promise<MapBranch | undefined> => {
        const selectedMapBranch = selectedBranches.find((branch) => branch.id === branchToResolve.id);
        const existingMapBranch = mapBranches.find((branch) => branch.id === branchToResolve.id);
        const dashboardBranch = dashboardBranches.find((branch) => branch.id === branchToResolve.id);
        const fetchedMapBranch =
            existingMapBranch ?? (isMapBranch(branchToResolve) ? branchToResolve : await fetchMapBranchById(branchToResolve.id));
        const fetchedDashboardBranch = dashboardBranch
            ?? (!isMapBranch(branchToResolve)
                ? branchToResolve
                : await fetchDashboardBranchById(branchToResolve.id).catch((error) => {
                    console.error('Failed to load dashboard branch details:', error);
                    return undefined;
                }));

        const hydratedBranch = mergeBranchData(
            fetchedMapBranch,
            selectedMapBranch,
            fetchedDashboardBranch
        ) as MapBranch;

        if (!hydratedBranch.latitude || !hydratedBranch.longitude) {
            return undefined;
        }

        return hydratedBranch;
    };

    const mergeSelectedBranchDetails = useCallback((branchDetails: Partial<MapBranch>) => {
        setMapBranches((prev) =>
            prev.map((branch) => branch.id === branchDetails.id ? { ...branch, ...branchDetails } : branch)
        );
        setSelectedBranches((prev) =>
            prev.map((branch) => branch.id === branchDetails.id ? { ...branch, ...branchDetails } : branch)
        );
        setSelectedBranch((prev) => prev.id === branchDetails.id ? { ...prev, ...branchDetails } : prev);
    }, []);

    const selectBranch = async (newBranch: MapBranch | BranchListItem) => {
        const selectedMapBranch = selectedBranches.find((branch) => branch.id === newBranch.id);
        const existingMapBranch = mapBranches.find((branch) => branch.id === newBranch.id);
        const dashboardBranch = dashboardBranches.find((branch) => branch.id === newBranch.id);
        const branchToSelect = isMapBranch(newBranch)
            ? mergeBranchData(newBranch, existingMapBranch, selectedMapBranch, dashboardBranch) as MapBranch
            : await getHydratedMapBranch(newBranch);

        if (!branchToSelect?.latitude || !branchToSelect?.longitude) {
            return;
        }

        setMapBranches((prev) => {
            if (prev.some((branch) => branch.id === branchToSelect.id)) {
                return prev.map((branch) => (
                    branch.id === branchToSelect.id
                        ? {
                            ...branch,
                            ...branchToSelect,
                        }
                        : branch
                ));
            }

            return [...prev, branchToSelect];
        });

        if (selectedBranches.some((branch) => branch.id === branchToSelect.id)) {
            setSelectedBranches([]);
            setSelectedBranch({} as MapBranch);
            setLastSelectedBranch(null);
            localStorage.removeItem('selectedBranch');
            localStorage.removeItem('markerID');
        } else {
            setSelectedBranch(branchToSelect);
            setNewMarker({ lat: +branchToSelect.latitude, lng: +branchToSelect.longitude });
            setSelectedBranches([branchToSelect]);
            setLastSelectedBranch(branchToSelect.id);
            localStorage.setItem('selectedBranch', JSON.stringify(branchToSelect));
        }

        if (isMapBranch(newBranch) && !dashboardBranch) {
            fetchDashboardBranchById(newBranch.id)
                .then((branchDetails) => {
                    mergeSelectedBranchDetails(branchDetails);
                    localStorage.setItem('selectedBranch', JSON.stringify({ ...branchToSelect, ...branchDetails }));
                })
                .catch((error) => {
                    console.error('Failed to load dashboard branch details:', error);
                });
        }
    };

    const applyBranchesFilter = (nextFilter: Filter) => {
        setBranchesFilter(nextFilter);
        setMapBranches(getFilteredMapBranches(nextFilter));
        setDashboardBranches([]);
        setBranches([]);
        setTaskManagerBranches([]);
        setDashboardPager(undefined);
        setSelectedBranches([]);
        setLastSelectedBranch(null);

        resourceCache(fetchDashboardBranches).clear();
        getNewDashboardBranches({ filter: getDashboardBranchesFilter(1, nextFilter) });
    };

    const filterBranchesByState = (state: string) => {
        applyBranchesFilter({
            ...branchesFilter,
            state: state || undefined,
        });
    };

    const filterBranchesByGroup = (group: string | undefined) => {
        applyBranchesFilter({
            ...branchesFilter,
            selectedGroup: group && group !== 'allGroups' ? group : undefined,
            assignedDistributor: undefined,
        });
    };

    const filterBranchesByUser = (id: number | undefined) => {
        applyBranchesFilter({
            ...branchesFilter,
            assignedDistributor: id ? String(id) : undefined,
        });
    };

    const filterBranchesBySearch = (search: string) => {
        applyBranchesFilter({
            ...branchesFilter,
            search,
        });
    };

    useEffect(() => {
        setMapBranches(getFilteredMapBranches(branchesFilter, mapBranchItems));
    }, [branchesFilter, getFilteredMapBranches, mapBranchItems]);

    useEffect(() => {
        setDashboardBranches(dashboardBranchItems);
        setBranches(dashboardBranchItems);
        setTaskManagerBranches(dashboardBranchItems);
        if (typeof dashboardBranchesData.pager?.page === 'number') {
            setDashboardPager(dashboardBranchesData.pager);
        }
    }, [dashboardBranchItems, dashboardBranchesData.pager]);

    const loadMoreDashboardBranches = useCallback(async () => {
        if (dashboardBranchesLoadingRef.current || !hasMoreDashboardBranches) {
            return;
        }

        const nextPage = (dashboardPager?.page ?? 1) + 1;
        dashboardBranchesLoadingRef.current = true;
        setDashboardBranchesLoading(true);

        try {
            const nextDashboardBranches = await fetchDashboardBranches({
                filter: getDashboardBranchesFilter(nextPage),
            });
            const nextItems = nextDashboardBranches.items ?? [];

            setDashboardPager(nextDashboardBranches.pager);
            setDashboardBranches((prev) => appendUniqueDashboardBranches(prev, nextItems));
            setBranches((prev) => appendUniqueDashboardBranches(prev, nextItems));
            setTaskManagerBranches((prev) => appendUniqueDashboardBranches(prev, nextItems));
        } finally {
            dashboardBranchesLoadingRef.current = false;
            setDashboardBranchesLoading(false);
        }
    }, [
        appendUniqueDashboardBranches,
        dashboardPager?.page,
        getDashboardBranchesFilter,
        hasMoreDashboardBranches,
    ]);

    /**
     * Add a new branch to local state optimistically
     * This avoids refetching all 5000+ branches from the server
     */
    const addBranchLocal = useCallback((branch: MapBranch) => {
        const dashboardBranch = toDashboardBranch(branch);
        setDashboardBranches(prev => [...prev, dashboardBranch]);
        setBranches(prev => [...prev, dashboardBranch]);
        setMapBranches(prev => (prev.some(existingBranch => existingBranch.id === branch.id) ? prev : [...prev, branch]));
        setTaskManagerBranches(prev => {
            if (prev.length < 30) {
                return [...prev, dashboardBranch];
            }
            return prev;
        });

        setSelectedBranches(prev => [...prev, branch]);
        setSelectedBranch(branch);
        setLastSelectedBranch(branch.id);
    }, [toDashboardBranch]);

    /**
     * Update an existing branch in local state optimistically
     */
    const updateBranchLocal = useCallback((updatedBranch: MapBranch) => {
        setDashboardBranches(prev =>
            prev.map(branch =>
                branch.id === updatedBranch.id ? { ...branch, ...updatedBranch } : branch
            )
        );

        setBranches(prev =>
            prev.map(branch =>
                branch.id === updatedBranch.id ? { ...branch, ...updatedBranch } : branch
            )
        );

        setMapBranches(prev =>
            prev.map(branch =>
                branch.id === updatedBranch.id ? { ...branch, ...updatedBranch } : branch
            )
        );

        setTaskManagerBranches(prev =>
            prev.map(branch =>
                branch.id === updatedBranch.id ? { ...branch, ...updatedBranch } : branch
            )
        );

        setSelectedBranches(prev =>
            prev.map(branch =>
                branch.id === updatedBranch.id ? updatedBranch : branch
            )
        );

        setSelectedBranch(prev => (prev.id === updatedBranch.id ? { ...prev, ...updatedBranch } : prev));
    }, []);

    /**
     * Remove a branch from local state optimistically
     */
    const removeBranchLocal = useCallback((branchId: number) => {
        setDashboardBranches(prev => prev.filter(branch => branch.id !== branchId));
        setBranches(prev => prev.filter(branch => branch.id !== branchId));
        setMapBranches(prev => prev.filter(branch => branch.id !== branchId));

        setTaskManagerBranches(prev => prev.filter(branch => branch.id !== branchId));

        setSelectedBranches(prev => prev.filter(branch => branch.id !== branchId));

        if (lastSelectedBranch === branchId) {
            setSelectedBranch({} as MapBranch);
            setLastSelectedBranch(null);
        }
    }, [lastSelectedBranch]);

    /**
     * Force refresh branches from server
     * Use this when you need to ensure data consistency (e.g., manual refresh button)
     * Branches automatically refresh on page load/navigation via existing useGetMapBranches hook
     */
    const refreshBranches = useCallback(() => {
        resourceCache(fetchMapBranches).clear();
        resourceCache(fetchDashboardBranches).clear();
        getNewBranches({ filter: getMapBranchesFilter() });
        getNewDashboardBranches({ filter: getDashboardBranchesFilter(1) });
    }, [getNewBranches, getNewDashboardBranches, getMapBranchesFilter, getDashboardBranchesFilter]);

    return (
        <BranchesContext.Provider
            value={{
                lastSelectedBranch,
                setLastSelectedBranch,
                branches,
                selectedBranch,
                selectedBranches,
                setSelectedBranches,
                dashboardBranches,
                mapBranches,
                setMapBranches,
                getMapBranches,
                branchesFilter,
                setBranchesFilter,
                selectBranch,
                taskManagerBranches,
                setTaskManagerBranches,
                loadMoreDashboardBranches,
                hasMoreDashboardBranches,
                dashboardBranchesLoading,
                filterBranchesByState,
                filterBranchesByGroup,
                filterBranchesByUser,
                filterBranchesBySearch,
                newMarker,
                setNewMarker,
                getReminders,
                fetchNewReminders,
                materialsPassports,
                devicePassports,
                getTroubles,
                fetchNewTroubles,
                getNewBranches,
                setBranches,
                addBranchLocal,
                updateBranchLocal,
                removeBranchLocal,
                refreshBranches
            }}
        >
            {children}
        </BranchesContext.Provider>
    );
};
